import { Button, ButtonMenu, Css, FullBleed, SelectField, Tag, useModal, useTestIds } from "@homebound/beam";
import { StatusIndicator } from "src/components/StatusIndicator";
import { useFullscreenModal } from "src/components/useFullscreenModal";
import { BidPackageStatus } from "src/generated/graphqlTypes";
import {
  BidPackageDetailPageDocument,
  PageBidPackageDetailFragment,
} from "src/routes/bids/BidPackageDetailPage.generated";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { PageHeaderBreadcrumbs } from "src/routes/layout/PageHeader";
import { createBidPackagesDetailUrl, createBidPackagesUrl, createPlanBasedBidPackageEditUrl } from "src/RouteUrls";
import { StringParam, useQueryParams } from "use-query-params";
import { EditBidPackageLineItemModal } from "../add-bid-package/components/BidPackageLineItemModal";
import { downloadPlanBasedBidPackageV2 } from "../plan-based-bid-package/components/3.ReviewAndDownloadStep";
import { AddTradePartnerModal } from "./AddTradePartnerModal";
import { AwardBidModal } from "./AwardBidModal";
import { useReviseBidPackageCostsMutation } from "./BidPackageHeader.generated";
import { UpdatesAvailableButton } from "./BidUpdatesAvailable";
import { DownloadBidModal } from "./DownloadBidModal";
import { EditBidPackageModal } from "./EditBidPackageModal";
import { useSaveBidPackageMutation } from "./EditBidPackageModal.generated";

type BidPackageHeaderProps = { bidPackage: PageBidPackageDetailFragment; displayingOldVersion?: boolean };

export function BidPackageHeader({ bidPackage, displayingOldVersion = false }: BidPackageHeaderProps) {
  const { id, name, projects, bidPackageGroup, status, availableUpdates } = bidPackage;
  const tid = useTestIds({});
  const { openFullscreen } = useFullscreenModal();
  const { openModal } = useModal();
  const [saveBidPackage] = useSaveBidPackageMutation({ refetchQueries: [BidPackageDetailPageDocument] });
  const [reviseCosts] = useReviseBidPackageCostsMutation({ refetchQueries: [BidPackageDetailPageDocument] });
  const isDraft = bidPackage.status.code === BidPackageStatus.Draft;
  const isAwarded = bidPackage.status.code === BidPackageStatus.Awarded;
  const hasUpdatesToReview = bidPackage.productOfferings.some((pof) =>
    availableUpdates.some((u) => u.source.readyPlan.id === pof.id),
  );
  const hasMissingOptions = bidPackage.optionsMissingFromPackage.nonEmpty;

  const handleArchive = (archive: boolean) => {
    openModal({
      content: (
        <ConfirmationModal
          confirmationMessage={
            <>
              Are you sure you want to {archive ? "archive" : "unarchive"} the bid package <b>{name}</b>?
            </>
          }
          onConfirmAction={async () => {
            await saveBidPackage({ variables: { input: { id: bidPackage.id, archive } } });
          }}
          title={archive ? "Archive Bid Package" : "Unarchive Bid Package"}
          label={archive ? "Archive" : "Unarchive"}
        />
      ),
    });
  };

  const handleReviseCosts = () => {
    openModal({
      content: (
        <ConfirmationModal
          confirmationMessage={
            <>
              Are you sure you want to revise costs for <b>{name}</b>? This will create a new version with the same
              scope and copied costs, allowing you to modify costs and re-award.
            </>
          }
          onConfirmAction={async () => {
            await reviseCosts({ variables: { input: { bidPackageId: bidPackage.id } } });
          }}
          title="Revise Costs"
          label="Revise Costs"
        />
      ),
    });
  };

  return (
    <FullBleed>
      <header css={Css.df.aic.py2.mhPx(64).bb.bw1.bcGray200.bgWhite.ifMdAndDown.fdc.$}>
        <div css={Css.w100.$}>
          <PageHeaderBreadcrumbs
            breadcrumb={[
              { href: createBidPackagesUrl(), label: "Bids" },
              { href: createBidPackagesUrl(projects.first?.id), label: `${projects.first?.name}` },
              {
                // TODO: Make bidPackageGroup required in schema on the BE (instead of forcing it here)
                // since this will always exist as a bidPackage parent
                href: createBidPackagesDetailUrl(bidPackageGroup!.id, id),
                label: name,
              },
            ]}
            linkXss={Css.blue700.cursorPointer.$}
          />

          {/** Customize our own header since the pageHeader component limits the ability to render header styles per the figma */}
          <div css={Css.dg.gtc("1fr auto").aic.$}>
            <h1 css={Css.xl.$} {...tid.name}>
              {name}
            </h1>

            <div css={Css.df.fdr.gap2.aic.$}>
              <div css={Css.df.fdc.wsnw.mr2.$}>
                <div css={Css.xs.gray700.$}>Status</div>
                <div css={Css.df.fdr.aic.gap1.$}>
                  <StatusIndicator status={status.code} type="BidPackage" />
                  <div {...tid.status}>{status.name}</div>
                  {bidPackage.isPlanBasedV2 &&
                    (hasMissingOptions ? (
                      <Tag icon="error" text="Add Missing Options" type="update" />
                    ) : hasUpdatesToReview ? (
                      <Tag icon="refresh" text="Review Scope Changes" type="info" />
                    ) : null)}
                </div>
              </div>
              {bidPackage.archivedAt ? (
                <div css={Css.mwPx(100).$}>
                  <Tag text="Archived" type="warning" />
                </div>
              ) : (
                !bidPackage.isPlanBasedV2 && <UpdatesAvailableButton bidPackageId={id} updates={availableUpdates} />
              )}
              {/* V2 plan-based packages don't support version history, so skip the selector. */}
              {!bidPackage.isPlanBasedV2 && <BidPackageVersionSelect bidPackage={bidPackage} />}
              <Button
                disabled={
                  displayingOldVersion ||
                  (isDraft ? undefined : disableBasedOnPotentialOperation(bidPackage.canComplete))
                }
                label={isDraft ? "Publish" : "Award Bid"}
                variant="primary"
                onClick={async () => {
                  if (isDraft) {
                    await saveBidPackage({
                      variables: { input: { id: bidPackage.id, status: BidPackageStatus.Open } },
                    });
                  } else {
                    openFullscreen(<AwardBidModal bidPackageId={id} />);
                  }
                }}
              />
              <ButtonMenu
                placement="right"
                trigger={{ icon: "verticalDots" }}
                items={[
                  {
                    label: "Edit Bid Package",
                    onClick: bidPackage.isPlanBasedV2
                      ? createPlanBasedBidPackageEditUrl(bidPackageGroup!.id, projects.first?.id)
                      : () => openFullscreen(<EditBidPackageModal bidPackage={bidPackage} />, true),
                    disabled: displayingOldVersion,
                  },
                  {
                    label: "Add Trade",
                    onClick: () => openModal({ content: <AddTradePartnerModal bidPackage={bidPackage} /> }),
                    disabled: displayingOldVersion,
                  },
                  ...(bidPackage.isPlanBasedV2
                    ? [
                        {
                          label: "Edit Bid Coverage",
                          onClick: () => openFullscreen(<EditBidPackageLineItemModal bidPackage={bidPackage} />, true),
                          disabled: displayingOldVersion,
                        },
                      ]
                    : []),
                  ...(!bidPackage.isInternalEstimate
                    ? [
                        {
                          label: "Download Bid Package",
                          onClick: () =>
                            bidPackage.isPlanBasedV2
                              ? downloadPlanBasedBidPackageV2(id)
                              : openModal({ content: <DownloadBidModal bidPackage={bidPackage} /> }),
                        },
                      ]
                    : []),
                  ...(isAwarded && !bidPackage.isPlanBasedV2 && !bidPackage.isUnitBased
                    ? [
                        {
                          label: "Revise Costs",
                          onClick: handleReviseCosts,
                          disabled: displayingOldVersion || disableBasedOnPotentialOperation(bidPackage.canReviseCosts),
                        },
                      ]
                    : []),
                  {
                    label: bidPackage.archivedAt ? "Unarchive Bid" : "Archive Bid",
                    onClick: () => handleArchive(!bidPackage.archivedAt),
                    disabled: displayingOldVersion,
                  },
                ]}
              />
            </div>
          </div>
        </div>
      </header>
    </FullBleed>
  );
}

function BidPackageVersionSelect({ bidPackage }: { bidPackage: PageBidPackageDetailFragment }) {
  const [{ versionId }, setQueryParams] = useQueryParams({ versionId: StringParam });
  return (
    <div css={Css.wPx(130).px1.$}>
      <SelectField
        label="version"
        value={versionId ?? bidPackage.latestVersion.id}
        options={bidPackage.versions}
        getOptionValue={(v) => v.id}
        getOptionLabel={(v) => v.displayVersion}
        onSelect={(versionId) => {
          setQueryParams({ versionId: versionId !== bidPackage.versions.last?.id ? versionId : undefined });
        }}
        labelStyle="hidden"
        borderless
        autoSort={false}
      />
    </div>
  );
}
